import imageCompression from 'browser-image-compression';

/**
 * Limite maximale de taille de fichier acceptée avant compression (en Mo)
 * Les fichiers plus volumineux seront rejetés
 */
const MAX_INPUT_SIZE_MB = 50;

/**
 * Limite maximale de taille de fichier après compression (en Mo)
 */
export const MAX_OUTPUT_SIZE_MB = 2;

/**
 * Résultat détaillé de la compression d'une image
 */
export interface CompressionResult {
    /** Fichier compressé (ou original si non compressé) */
    file: File;
    /** Taille originale en octets */
    originalSize: number;
    /** Taille compressée en octets */
    compressedSize: number;
    /** Octets économisés */
    savedBytes: number;
    /** Pourcentage de réduction (0-100) */
    savedPercentage: number;
    /** Indique si le format a été converti */
    wasConverted: boolean;
    /** Format d'entrée (ex: "image/png") */
    inputFormat: string;
    /** Format de sortie (ex: "image/webp") */
    outputFormat: string;
    /** Indique si une compression a été effectuée */
    wasCompressed: boolean;
    /** Message d'erreur éventuel */
    error?: string;
}

/**
 * Vérifie si le navigateur supporte le format WebP
 * @returns true si WebP est supporté
 */
function supportsWebP(): boolean {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        return canvas.toDataURL('image/webp').startsWith('data:image/webp');
    } catch {
        return false;
    }
}

// Cache du support WebP pour éviter de recalculer à chaque appel
let webpSupportCached: boolean | null = null;

/**
 * Vérifie si le navigateur supporte WebP (avec cache)
 */
function getWebPSupport(): boolean {
    if (webpSupportCached === null) {
        webpSupportCached = supportsWebP();
    }
    return webpSupportCached;
}

/**
 * Détermine le format de sortie optimal
 * @param inputType - Type MIME du fichier d'entrée
 * @returns Type MIME de sortie recommandé
 */
function getOptimalOutputFormat(inputType: string): string {
    // Préserver SVG, GIF et PNG (transparence)
    if (inputType === 'image/svg+xml' || inputType === 'image/gif' || inputType === 'image/png') {
        return inputType;
    }

    // Utiliser WebP si supporté, sinon JPEG
    return getWebPSupport() ? 'image/webp' : 'image/jpeg';
}

/**
 * Obtient l'extension de fichier correspondant au type MIME
 */
function getExtensionFromMimeType(mimeType: string): string {
    const extensions: Record<string, string> = {
        'image/webp': 'webp',
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
    };
    return extensions[mimeType] || 'jpg';
}

/**
 * Options de compression d'image par défaut
 */
const DEFAULT_OPTIONS = {
    maxSizeMB: MAX_OUTPUT_SIZE_MB,
    maxWidthOrHeight: 2048,
    useWebWorker: true,
    initialQuality: 0.85,
};

/**
 * Compresse une image et retourne un résultat détaillé
 *
 * Fonctionnalités :
 * - Convertit automatiquement en WebP (ou JPEG si non supporté)
 * - Réduit la taille à max 2 Mo
 * - Redimensionne si > 2048px
 * - Préserve les SVG et GIF (pas de compression)
 * - Utilise Web Worker (non-bloquant)
 *
 * @param file - Fichier à compresser
 * @param options - Options de compression (optionnel)
 * @returns Promise<CompressionResult> - Résultat détaillé de la compression
 */
export async function compressImageWithDetails(
    file: File,
    options: Partial<typeof DEFAULT_OPTIONS> = {}
): Promise<CompressionResult> {
    const originalSize = file.size;
    const inputFormat = file.type;

    // Créer un résultat par défaut (non compressé)
    const createResult = (
        outputFile: File,
        wasCompressed: boolean,
        outputFormat: string,
        error?: string
    ): CompressionResult => ({
        file: outputFile,
        originalSize,
        compressedSize: outputFile.size,
        savedBytes: originalSize - outputFile.size,
        savedPercentage: getCompressionPercentage(originalSize, outputFile.size),
        wasConverted: inputFormat !== outputFormat,
        inputFormat,
        outputFormat,
        wasCompressed,
        error,
    });

    // Ne pas compresser si ce n'est pas une image
    if (!file.type.startsWith('image/')) {
        return createResult(file, false, inputFormat);
    }

    // Vérifier la taille maximale acceptée
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_INPUT_SIZE_MB) {
        return createResult(
            file,
            false,
            inputFormat,
            `Ce fichier est trop volumineux (${formatFileSize(file.size)}). Veuillez utiliser une image de moins de ${MAX_INPUT_SIZE_MB} Mo.`
        );
    }

    // Ne pas compresser les SVG (fichiers vectoriels), GIF (animations) ni PNG (transparence)
    if (file.type === 'image/svg+xml' || file.type === 'image/gif' || file.type === 'image/png') {
        return createResult(file, false, inputFormat);
    }

    // Déterminer le format de sortie optimal
    const outputFormat = getOptimalOutputFormat(inputFormat);
    const outputExtension = getExtensionFromMimeType(outputFormat);

    // Fusionner les options par défaut avec celles fournies
    const compressionOptions = {
        ...DEFAULT_OPTIONS,
        ...options,
        fileType: outputFormat,
    };

    try {
        console.log(`🖼️ Compression de l'image : ${file.name} (${formatFileSize(file.size)})`);

        const compressedFile = await imageCompression(file, compressionOptions);

        // `imageCompression()` peut retourner un Blob (pas un File) selon les versions de la lib.
        // Si on l'envoie tel quel à FormData.append('file', blob), le navigateur utilise "blob"
        // comme filename par défaut et on perd le nom original. On wrap donc systématiquement
        // dans un File avec le nom approprié.
        const targetName = inputFormat !== outputFormat
            ? file.name.replace(/\.[^/.]+$/, `.${outputExtension}`)
            : file.name;
        const finalFile = new File([compressedFile], targetName, {
            type: inputFormat !== outputFormat ? outputFormat : compressedFile.type || file.type,
        });

        // Vérifier que la compression a bien fonctionné
        if (finalFile.size > MAX_OUTPUT_SIZE_MB * 1024 * 1024) {
            console.warn(`⚠️ Image toujours > ${MAX_OUTPUT_SIZE_MB} Mo après compression`);
            return createResult(
                finalFile,
                true,
                outputFormat,
                `Impossible de réduire cette image en dessous de ${MAX_OUTPUT_SIZE_MB} Mo. Veuillez utiliser une image moins volumineuse ou de résolution inférieure.`
            );
        }

        const result = createResult(finalFile, true, outputFormat);

        console.log(
            `✅ Image compressée : ${file.name}\n` +
            `   Original : ${formatFileSize(result.originalSize)} (${inputFormat})\n` +
            `   Compressé : ${formatFileSize(result.compressedSize)} (${outputFormat})\n` +
            `   Réduction : ${result.savedPercentage}%`
        );

        return result;
    } catch (error) {
        console.error('❌ Erreur lors de la compression:', error);
        return createResult(
            file,
            false,
            inputFormat,
            'Erreur lors de la compression de l\'image.'
        );
    }
}

/**
 * Compresse une image avant upload (version simplifiée)
 *
 * @param file - Fichier à compresser
 * @param options - Options de compression (optionnel)
 * @returns Promise<File> - Fichier compressé ou original
 *
 * @example
 * const compressedFile = await compressImage(file);
 */
export async function compressImage(
    file: File,
    options: Partial<typeof DEFAULT_OPTIONS> = {}
): Promise<File> {
    const result = await compressImageWithDetails(file, options);
    return result.file;
}

/**
 * Formatte une taille de fichier en octets en une chaîne lisible
 *
 * @param bytes - Taille en octets
 * @returns string - Taille formatée (ex: "1.5 Mo")
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Octets';

    const k = 1024;
    const sizes = ['Octets', 'Ko', 'Mo', 'Go', 'To'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Calcule le pourcentage de réduction de taille
 *
 * @param originalSize - Taille originale en octets
 * @param compressedSize - Taille compressée en octets
 * @returns number - Pourcentage de réduction (0-100)
 */
function getCompressionPercentage(originalSize: number, compressedSize: number): number {
    if (originalSize === 0) return 0;
    return Math.round(((originalSize - compressedSize) / originalSize) * 100);
}

/**
 * Vérifie si un fichier est une image compressible
 * (exclut SVG, GIF et PNG)
 */
export function isCompressibleImage(file: File): boolean {
    return (
        file.type.startsWith('image/') &&
        file.type !== 'image/svg+xml' &&
        file.type !== 'image/gif' &&
        file.type !== 'image/png'
    );
}

/**
 * Retourne un message de format lisible pour le type MIME
 */
export function getFormatDisplayName(mimeType: string): string {
    const names: Record<string, string> = {
        'image/webp': 'WebP',
        'image/jpeg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/svg+xml': 'SVG',
    };
    return names[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'Inconnu';
}
